<template>
	<div class="flex flex-wrap gap-2">
		<div
			v-for="(photo, index) in photos"
			:key="index"
			class="group relative h-24 w-24 cursor-pointer overflow-hidden rounded-lg bg-gray-100"
			@click="handlePhotoClick(index)"
		>
			<!-- Photo image -->
			<img
				:src="photo.thumbnailSrc || photo.fullSrc"
				:alt="photo.name"
				class="h-full w-full object-cover"
				:loading="lazy ? 'lazy' : 'eager'"
			/>

			<!-- Photo index badge -->
			<div
				v-if="showIndex"
				class="absolute left-1 top-1 rounded bg-black bg-opacity-50 px-1 py-0.5 text-xs text-white"
			>
				{{ index + 1 }}
			</div>

			<!-- Delete Mode Overlay -->
			<div
				v-if="deleteMode"
				class="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-75"
			>
				<ion-icon :icon="trashOutline" class="text-2xl text-white" />
			</div>

			<!-- View Mode Overlay (on hover) -->
			<div
				v-else-if="showHoverOverlay"
				class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
			>
				<ion-icon :icon="eyeOutline" class="text-2xl text-white" />
			</div>
		</div>

		<!-- Add photos button slot -->
		<slot name="add-button" />
	</div>

	<!-- Image Viewer Modal -->
	<image-viewer-modal
		v-model="isViewerOpen"
		:image-src="selectedImageSrc"
		:title="selectedImageTitle"
	/>
</template>

<script setup lang="ts">
	import { ref } from "vue";
	import { IonIcon } from "@ionic/vue";
	import { trashOutline, eyeOutline } from "ionicons/icons";
	import ImageViewerModal from "@/components/ui/modals/ImageViewerModal.vue";

	/**
	 * Generic photo interface for display.
	 * Parent components should transform their data to this shape.
	 */
	export interface DisplayPhoto {
		/** Thumbnail URL for grid display (optional, falls back to fullSrc) */
		thumbnailSrc?: string;
		/** Full-size URL for viewer modal */
		fullSrc: string;
		name: string;
	}

	const props = withDefaults(
		defineProps<{
			photos: DisplayPhoto[];
			deleteMode?: boolean;
			showIndex?: boolean;
			showHoverOverlay?: boolean;
			lazy?: boolean;
		}>(),
		{
			deleteMode: false,
			showIndex: false,
			showHoverOverlay: true,
			lazy: true,
		},
	);

	const emit = defineEmits<{
		photoClick: [index: number, photo: DisplayPhoto];
	}>();

	const isViewerOpen = ref(false);
	const selectedImageSrc = ref("");
	const selectedImageTitle = ref("Image");

	const viewPhoto = (index: number) => {
		const photo = props.photos[index];
		selectedImageSrc.value = photo.fullSrc;
		selectedImageTitle.value = photo.name;
		isViewerOpen.value = true;
	};

	const handlePhotoClick = (index: number) => {
		if (props.deleteMode) {
			emit("photoClick", index, props.photos[index]);
		} else {
			viewPhoto(index);
		}
	};
</script>
